import React, { useEffect, useState, useMemo, useRef } from 'react';
import { BhiDialog } from 'src/UIKit/components/Dialog';
import {
  Box,
  Button,
  CircularProgress,
  debounce,
  Grid,
  TextField,
  Typography,
  DialogContent,
  DialogActions,
  IconButton,
  Card,
  CardContent,
} from '@mui/material';
import { 
  Close as CloseIcon, 
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { useAssetsApi } from 'src/modules/assetLibrary/api/AssetsApi';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { FileLinkWithMimeType } from 'src/constants/types';
import { MediaInfo } from 'src/modules/assetLibrary/types';
import ChatFileIcon from 'src/components/ChatFileIcon/view';
import { FileDisplayLabel } from 'src/components/FileDisplayLabel';
import { getFileDisplayNameAndExtension } from 'src/helpers/files';
import VcfDisplayFromUrl from 'src/components/VcfViewer/view';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import EventIcon from '@mui/icons-material/Event';

const StyledAssetCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  height: theme.breakpoints.down('md') ? '120px' : '150px',
  '&:hover': {
    boxShadow: theme.customShadows.card,
  },
  '&:hover .asset-actions': {
    opacity: 1,
  },
}));

const AssetActions = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.breakpoints.down('md') ? 4 : 8,
  right: theme.breakpoints.down('md') ? 4 : 8,
  opacity: 0,
  transition: 'opacity 0.2s',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  borderRadius: theme.shape.borderRadius,
  padding: theme.breakpoints.down('md') ? '1px' : '2px',
}));

const AssetNameContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.breakpoints.down('md') ? '2px' : '4px',
  borderRadius: theme.shape.borderRadius,
  transition: 'all 0.2s',
  cursor: 'pointer',
  minHeight: theme.breakpoints.down('md') ? '20px' : '24px',
  marginBottom: theme.breakpoints.down('md') ? '15px' : '20px',
  '& h6': {
    width: '100%',
    textAlign: 'center',
    fontSize: theme.breakpoints.down('md') ? '12px' : '16px',
  },
}));

interface AssetLibraryProps {
  onSelectAsset: (asset: FileLinkWithMimeType) => void;
  onClose: () => void;
  isMobile?: boolean;
  filterVcfOnly?: boolean;
}

export const AssetLibrary: React.FC<AssetLibraryProps> = ({ onSelectAsset, onClose, isMobile, filterVcfOnly = false }) => {
  const selectedCompanyId = useSelector(getCurrentCompanyId);
  const [assets, setAssets] = useState<MediaInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [previewAsset, setPreviewAsset] = useState<MediaInfo | null>(null);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { getAssets } = useAssetsApi();

  const fetchAssets = async (query?: string) => {
    if (!selectedCompanyId) return;

    setLoading(true);
    try {
      const mimeTypeFilter = filterVcfOnly ? 'text/vcard' : undefined;
      const response = await getAssets({
        companyId: selectedCompanyId,
        query,
        sort: 'date_created_desc',
        limit: 1000,
        offset: 0,
        mime_type: mimeTypeFilter,
      });

      setAssets(response.data.items);
      setTotalCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch assets:', error);
      setAssets([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetchAssets = useMemo(
    () => debounce((query: string) => fetchAssets(query), 300),
    [selectedCompanyId, filterVcfOnly]
  );

  useEffect(() => {
    fetchAssets();
  }, [selectedCompanyId, filterVcfOnly]);

  useEffect(() => {
    if (searchQuery) {
      debouncedFetchAssets(searchQuery);
    } else {
      fetchAssets();
    }
  }, [searchQuery, debouncedFetchAssets]);

  const handleAssetClick = (asset: MediaInfo) => {
    onSelectAsset({
      url: asset.url,
      mimeType: asset.mimeType,
      name: asset.name,
    });
    onClose();
  };

  const handlePreview = (asset: MediaInfo) => {
    setPreviewAsset(asset);
  };

  const handleImageError = (assetId: string) => {
    setFailedImages(prev => new Set(prev).add(assetId));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleAssetCardClick = (e: React.MouseEvent, asset: MediaInfo) => {
    e.stopPropagation();
    handleAssetClick(asset);
  };
  
  const renderAssetCard = (asset: MediaInfo) => {
    const { name: displayName, extension: extensionFromMimeType } = getFileDisplayNameAndExtension(asset.name, asset.mimeType);

    return (
      <StyledAssetCard>
        <CardContent
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            padding: '16px',
            '&:last-child': {
              paddingBottom: '16px',
            },
          }}
        >
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100px',
              marginBottom: '5px',
              overflow: 'hidden',
              borderRadius: 1,
              backgroundColor: 'grey.100',
              padding: '8px',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'grey.200',
              },
            }}
            onClick={(e) => handleAssetCardClick(e, asset)}
          >
            {asset.mimeType.startsWith('image/') && !failedImages.has(asset.id) ? (
              <img
                src={asset.url}
                alt={asset.name || 'Asset'}
                style={{
                  maxWidth: '100%',
                  maxHeight: '120px',
                  objectFit: 'contain',
                  borderRadius: '4px',
                }}
                onError={() => handleImageError(asset.id)}
              />
            ) : asset.mimeType === 'text/vcard' || asset.mimeType === 'text/x-vcard' ? (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  '& > div': {
                    padding: '8px !important',
                    margin: '0 !important',
                    border: 'none !important',
                    borderRadius: '0 !important',
                    minHeight: 'auto !important',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  },
                  '& h6': {
                    fontSize: '0.875rem',
                    fontWeight: '600 !important',
                    marginBottom: '4px !important',
                    lineHeight: '1.2 !important',
                  },
                  '& p': {
                    fontSize: '0.75rem',
                    margin: '1px 0 !important',
                    lineHeight: '1.1 !important',
                  },
                  '& a': {
                    fontSize: '0.75rem !important',
                    lineHeight: '1.1 !important',
                  },
                }}
              >
                <VcfDisplayFromUrl vcfUrl={asset.url} fontSize='10px' />
              </Box>
            ) : (
              <ChatFileIcon url={asset.url} mimeType={asset.mimeType} />
            )}
          </Box>

          <Box sx={{ flexGrow: 1 }}>
            <AssetNameContainer>
              <FileDisplayLabel
                name={displayName}
                extension={extensionFromMimeType}
                sx={{
                  ...((!asset.name || asset.name === 'Untitled') && {
                    color: 'text.secondary',
                    fontStyle: 'italic',
                  }),
                }}
                onClick={() => handleAssetClick(asset)}
              />
            </AssetNameContainer>
          </Box>
        </CardContent>

        {!isMobile && <AssetActions className="asset-actions">
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              handlePreview(asset);
            }} 
            color="primary" 
            sx={{ mr: 0.5 }}
          >
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </AssetActions>}
      </StyledAssetCard>
    );
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <TextField
        fullWidth
        size={isMobile ? 'small' : 'medium'}
        placeholder="Search assets..."
        value={searchQuery}
        onChange={handleSearchChange}
        inputRef={searchInputRef}
        sx={{ 
          marginBottom: isMobile ? '15px' : '20px',
          '& .MuiInputBase-input': {
            fontSize: isMobile ? '14px' : '16px',
          }
        }}
      />
      
      <Box sx={{ 
        minHeight: isMobile ? '300px' : '400px', 
        maxHeight: isMobile ? '300px' : '400px', 
        overflow: 'auto', 
        minWidth: '100%' 
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: isMobile ? '150px' : '200px' }}>
            <CircularProgress />
          </Box>
        ) : assets.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: isMobile ? '150px' : '200px' }}>
            <Typography variant={isMobile ? 'body2' : 'body1'} color="text.secondary">
              {searchQuery ? 'No assets found matching your search.' : 'No assets available.'}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={isMobile ? 1.5 : 3} paddingTop={1} paddingBottom={1} paddingLeft={isMobile ? 0.5 : 1} paddingRight={isMobile ? 0.5 : 1}>
            {assets.map((asset, index) => (
              <Grid item xs={isMobile ? 4 : 12} sm={6} md={4} key={`${asset.id}-${index}`}>
                {renderAssetCard(asset)}
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
      
      {totalCount > 0 && (
        <Typography variant={isMobile ? 'caption' : 'body2'} color="text.secondary" sx={{
          marginTop: isMobile ? '8px' : '10px',
        }}>
          {totalCount} asset{totalCount !== 1 ? 's' : ''} total
        </Typography>
      )}

      <BhiDialog
        open={!!previewAsset}
        onClose={() => setPreviewAsset(null)}
        maxWidth={isMobile ? false : 'md'}
        fullWidth={isMobile}
        title="Preview Asset"
        showCloseButton={false}
        sx={{
          '& .MuiDialog-paper': {
            margin: isMobile ? '16px' : 'auto',
            width: isMobile ? 'calc(100% - 32px)' : 'auto',
            maxWidth: isMobile ? 'none' : '600px',
          }
        }}
      >
        <Box sx={{
          fontSize: isMobile ? '18px' : '20px',
          padding: isMobile ? '16px' : '24px'
        }}>
          <IconButton
            aria-label="close"
            onClick={() => setPreviewAsset(null)}
            sx={{
              position: 'absolute',
              right: isMobile ? 4 : 8,
              top: isMobile ? 4 : 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ padding: isMobile ? '16px' : '24px' }}>
          {previewAsset && (
            <Box sx={{ textAlign: 'center', p: isMobile ? 1 : 2 }}>
              {previewAsset.mimeType.startsWith('image/') ? (
                <img
                  src={previewAsset.url}
                  alt={previewAsset.name || 'Asset'}
                  style={{
                    maxWidth: '100%',
                    maxHeight: isMobile ? '50vh' : '70vh',
                    objectFit: 'contain',
                  }}
                />
              ) : (previewAsset.mimeType === 'text/vcard' || previewAsset.mimeType === 'text/x-vcard') ? (
                <Box sx={{ p: isMobile ? 1 : 2 }}>
                  <VcfDisplayFromUrl vcfUrl={previewAsset.url} fontSize='' />
                </Box>
              ) : previewAsset.mimeType === 'text/calendar' ? (
                <Box sx={{ p: isMobile ? 2 : 4 }}>
                  <EventIcon sx={{ 
                    fontSize: isMobile ? 48 : 64, 
                    color: 'text.secondary', 
                    mb: isMobile ? 1 : 2 
                  }} />
                  <Typography variant="h6" sx={{ 
                    mb: isMobile ? 1 : 2,
                    fontSize: isMobile ? '18px' : '20px'
                  }}>
                    {previewAsset.name || 'Calendar Event'}
                  </Typography>
                  <Typography variant={isMobile ? 'body2' : 'body1'} color="text.secondary" sx={{
                    mb: isMobile ? 2 : 3,
                  }}>
                  No preview available for calendar events.
                  </Typography>
                  <Button
                    variant="contained"
                    href={previewAsset.url}
                    download={previewAsset.name}
                    sx={{ 
                      mt: isMobile ? 1 : 2,
                      fontSize: isMobile ? '14px' : '16px'
                    }}
                  >
                    Download Calendar File
                  </Button>
                </Box>
              ) : previewAsset.mimeType.startsWith('video/') ? (
                <video
                  controls
                  style={{
                    maxWidth: '100%',
                    maxHeight: isMobile ? '50vh' : '70vh',
                  }}
                >
                  <source src={previewAsset.url} type={previewAsset.mimeType} />
                </video>
              ) : previewAsset.mimeType.startsWith('audio/') ? (
                <Box>
                  <audio controls style={{ 
                    width: '100%', 
                    marginBottom: isMobile ? '12px' : '16px' 
                  }}>
                    <source src={previewAsset.url} type={previewAsset.mimeType} />
                  </audio>
                  <Typography variant={isMobile ? 'body2' : 'body1'}>
                    Audio file: {previewAsset.name}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ p: isMobile ? 2 : 4 }}>
                  <ChatFileIcon url={previewAsset.url} mimeType={previewAsset.mimeType} />
                  <Typography variant="h6" sx={{ 
                    mt: isMobile ? 1 : 2,
                    fontSize: isMobile ? '18px' : '20px'
                  }}>
                    {previewAsset.name}
                  </Typography>
                  <Typography variant={isMobile ? 'body2' : 'body1'} color="text.secondary">
                    File Type: {previewAsset.mimeType}
                  </Typography>
                  <Button
                    variant="contained"
                    href={previewAsset.url}
                    download={previewAsset.name}
                    sx={{ 
                      mt: isMobile ? 1 : 2,
                      fontSize: isMobile ? '14px' : '16px'
                    }}
                  >
                    Download File
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            variant='contained' 
            onClick={() => setPreviewAsset(null)}
            sx={{ fontSize: isMobile ? '14px' : '16px' }}
          >
            Close
          </Button>
        </DialogActions>
      </BhiDialog>
    </Box>
  );
}; 
